import { XMarkIcon } from '@heroicons/react/24/solid'
import { withZod } from '@remix-validated-form/with-zod'
import { useEffect, useState } from 'react'
import { useFieldArray } from 'remix-validated-form'
import { z } from 'zod'
import { AddNewWorkspaceIntegrationModal } from '../AddNewWorkspaceIntegrationModal'
import Button from 'app/components/Button'
import { useConfirmation } from 'app/components/ConfirmationContext'
import FormInput from 'app/components/form/FormInput'
import FormSelect from 'app/components/form/FormComboSelect'
import { SmartForm } from 'app/components/form/SmartForm'
import Spinner from 'app/components/Spinner'
import { Tooltip } from 'app/components/Tooltip'
import type { LocationJson } from 'app/types/entities'
import { closeModal, showModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { bg } from 'lib/background'
import { useLocationStore } from 'app/stores/StoreContext'

const createSchema = z.object({
  apiKey: z.string().min(1, { message: `An API key is required` }),
  defaultLocationId: z.string().min(1, { message: `A Location is required` }),
  routingRules: z
    .array(
      z.object({
        locationId: z.string().min(1, { message: `A Location is required` }),
        formId: z.string().optional(),
        field: z
          .object({
            name: z.string().min(1, { message: `A field name is required` }),
            value: z.string().min(1, { message: `A field value is required` }),
          })
          .optional(),
      }),
    )
    .optional(),
})

// On edit the apiKey is not sent to the browser; the field is pre-filled with a mask and
// leaving the mask in place keeps the stored key.
const editSchema = createSchema.extend({ apiKey: z.string().optional() })

// Must match SECRET_MASK in the api integrations route.
const SECRET_MASK = `••••••••`

export const createGravityFormWorkspaceIntegrationValidator = withZod(createSchema)
export const editGravityFormWorkspaceIntegrationValidator = withZod(editSchema)

export function GravityFormWorkspaceModal({
  onIntegrationChanged,
  currentValues,
}: {
  onIntegrationChanged: () => void
  currentValues?: {
    apiKeySet?: boolean
    defaultLocationId: string
    routingRules?: {
      locationId: string
      formId?: string
      field?: {
        name: string
        value: string
      }
    }[]
  }
}) {
  const [confirm] = useConfirmation()
  const [items, { push, remove }] = useFieldArray(`routingRules`, {
    formId: `integration-form`,
  })
  const [locationOptions, setLocationOptions] = useState<{ label: string; value: string }[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const locationStore = useLocationStore()

  useEffect(() => {
    bg(async () => {
      const result = await locationStore.fetchList()
      const data = result.data as any
      const hits: LocationJson[] = data?.locations?.hits ?? data?.hits ?? locationStore.hits
      const locations = hits
        .filter((location) => location.name && location.id)
        .map((location) => ({
          label: location.name as string,
          value: location.id as string,
        }))
      setLocationOptions(locations)
      setIsLoading(false)
    })
  }, [])

  const onCreateCompleted = (data: any) => {
    if (data.success) {
      notifySuccess(`GravityForm workspace integration created`)
      onIntegrationChanged()
      closeModal()
    } else {
      notifyError(`Failed to create integration: ${data.error}`)
    }
  }

  const goBackToSelectIntegrationModal = () => {
    closeModal()
    showModal(
      <AddNewWorkspaceIntegrationModal onIntegrationChanged={onIntegrationChanged} />,
      {
        title: `Add New Workspace Integration`,
        isFullScreen: false,
        overflowHidden: false,
        removePadding: true,
        useRadix: true,
      },
      false,
    )
  }

  const onRemove = () => {
    return confirm({
      title: `Remove GravityForm Workspace Integration?`,
      description: `Removing this workspace integration will also remove all associated data. Do you want to continue?`,
      variant: `danger`,
    }).then(() => {
      return window
        .fetch(`/api/integrations/setup/gravityforms`, {
          method: `DELETE`,
          credentials: `include`,
          headers: { 'Content-Type': `application/json` },
          body: JSON.stringify({}),
        })
        .then(() => {
          notifySuccess(`GravityForm workspace integration removed`)
          closeModal()
          onIntegrationChanged()
        })
        .catch((error) => {
          notifyError(`Failed to remove integration: ${error}`)
        })
    })
  }

  return (
    <>
      {isLoading ? (
        <div className="flex h-96 w-[1000px] items-center justify-center">
          <Spinner size={24} />
        </div>
      ) : (
        <SmartForm
          id="integration-form"
          className="!mt-0 max-h-[800px] w-[1000px] !max-w-[1000px] grow-0 justify-start !gap-y-4 overflow-y-auto p-4"
          validator={
            currentValues
              ? editGravityFormWorkspaceIntegrationValidator
              : createGravityFormWorkspaceIntegrationValidator
          }
          reloadDocument={false}
          shouldPreventDefault={true}
          onPreventDefault={(data) => onCreateCompleted(data)}
          action="/api/integrations/setup/gravityforms"
          hideButtons={true}
          method={currentValues ? `PUT` : `POST`}
          defaultValues={
            currentValues ? { ...currentValues, apiKey: currentValues.apiKeySet ? SECRET_MASK : undefined } : undefined
          }
        >
          <FormInput
            name="apiKey"
            cols={6}
            required={!currentValues}
            label="Webhook API Key"
            labelClassName="text-body-sm text-strong"
            newLineError={false}
            showErrorIcon
          />
          <FormSelect
            name="defaultLocationId"
            cols={6}
            label="Default Location"
            options={locationOptions}
            labelClassName="text-body-sm text-strong"
            // onChange={(val) => setDefaultLocation(val)}
            defaultValue={
              currentValues
                ? locationOptions.find((option) => option.value === currentValues.defaultLocationId)?.value
                : undefined
            }
          />
          {/* <FormCombo
            cols={6}
            name="defaultLocationId"
            required={true}
            showSearchInDropdown={true}
            label="Default Location"
            defaultValue={
              currentValues
                ? locationOptions.find(
                    (option) => option.value === currentValues.gravityFormsMultiConfig.defaultLocationId,
                  )
                : undefined
            }
            value={undefined}
            options={locationOptions}
            labelClassName="text-body-sm text-trn-900"
            // onChange={(val) => setDefaultLocation(val)}
            newLineError={false}
            showErrorIcon
          /> */}
          <p className="col-span-6 my-3 text-ui-lg">Location Mapping</p>
          {items.map(({ defaultValue, key }, index) => (
            <div key={key} className="col-span-6 grid grid-cols-8 gap-3">
              <FormSelect
                name={`routingRules[${index}].locationId`}
                cols={3}
                label="Location"
                options={locationOptions}
                labelClassName="text-body-sm text-strong"
                // onChange={(val) => setDefaultLocation(val)}
                defaultValue={
                  currentValues
                    ? locationOptions.find((option) => option.value === currentValues.routingRules?.[index]?.locationId)
                        ?.value
                    : undefined
                }
              />
              {(defaultValue?.formId || defaultValue?.type === `form-mapping`) && (
                <>
                  <FormInput
                    type="text"
                    cols={4}
                    name={`routingRules[${index}].formId`}
                    label="Form ID"
                    labelClassName="text-body-sm text-strong"
                    className="!bg-surface"
                    value={defaultValue?.formId}
                    newLineError={false}
                    showErrorIcon
                  />
                </>
              )}
              {(defaultValue?.field || defaultValue?.type === `field-mapping`) && (
                <>
                  <FormInput
                    type="text"
                    cols={2}
                    name={`routingRules[${index}].field.name`}
                    label="Field Name"
                    labelClassName="text-body-sm text-strong"
                    className="!bg-surface"
                    value={defaultValue?.formId}
                    newLineError={false}
                    showErrorIcon
                  />
                  <FormInput
                    type="text"
                    cols={2}
                    name={`routingRules[${index}].field.value`}
                    label="Field Value"
                    labelClassName="text-body-sm text-strong"
                    className="!bg-surface"
                    value={defaultValue?.formId}
                    newLineError={false}
                    showErrorIcon
                  />
                </>
              )}
              <div className="col-span-1 flex items-center justify-start">
                <XMarkIcon
                  className="mt-6 h-6 w-6 cursor-pointer text-red-500"
                  onClick={() => {
                    remove(index)
                  }}
                />
              </div>
            </div>
          ))}
          <Button
            onClick={() => push({ type: `form-mapping` })}
            className="col-span-2 col-start-2"
            label="+ New Form Mapping"
            mode="primary"
          />
          <Button
            onClick={() => push({ type: `field-mapping` })}
            className="col-span-2"
            label="+ New Field Mapping"
            mode="primary"
          />
        </SmartForm>
      )}
      <div className="sticky bottom-0 w-full rounded-b-lg border-y border-default bg-surface-raised p-4">
        <div className="flex justify-between align-middle">
          <div>
            {currentValues ? (
              <Tooltip message={undefined}>
                <Button label="Remove" mode="danger" size="lg" onClick={() => onRemove()} />
              </Tooltip>
            ) : null}
          </div>
          <div className="flex justify-end gap-x-4 align-middle">
            {!currentValues ? (
              <Button label="Back" mode="white" size="lg" onClick={() => goBackToSelectIntegrationModal()} />
            ) : (
              <Button label="Cancel" mode="white" size="lg" onClick={() => closeModal()} />
            )}

            <Button
              type="submit"
              form="integration-form"
              label="Save"
              mode="primary"
              size="lg"
              // onClick={() => setSaving(true)}
              // loading={saving}
            />
          </div>
        </div>
      </div>
    </>
  )
}
